import React, { useState, useEffect } from 'react';
import {
    X,
    AlertCircle,
    Plus,
    RefreshCw,
    Save
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
    sanitizeTextInput,
    validateNumberInput,
    FIELD_LIMITS
} from '../../../utils/inputValidation';
import { toast } from 'react-hot-toast';
import { leadSourceService, companySizeService } from '../../../api/crm/config/configService';

const ScoringRuleModal = ({
    isOpen,
    onClose,
    selectedScoring,
    onSave,
    isSaving,
    scoringFormData,
    setScoringFormData,
    scoringErrors,
    setScoringErrors
}) => {
    const [newRule, setNewRule] = useState({ field: 'source', operator: 'equals', condition: '', score: '' });
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);
    const [companySizeOptions, setCompanySizeOptions] = useState([]);

    useEffect(() => {
        const fetchLeadSources = async () => {
            try {
                const response = await leadSourceService.getAll({ isActive: true });
                if (response.success && Array.isArray(response.data)) {
                    const options = response.data.map(source => ({
                        value: source.name,
                        label: source.name
                            .split(/_|\s+/)
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                    }));
                    setLeadSourceOptions(options);
                }
            } catch (error) {
                toast.error('Failed to load lead sources');
            }
        };
        const fetchCompanySizes = async () => {
            try {
                const response = await companySizeService.getAll({ isActive: true });
                if (response.success && Array.isArray(response.data)) {
                    const options = response.data.map(size => ({
                        value: size._id,
                        label: size.name
                    }));
                    setCompanySizeOptions(options);
                }
            } catch (error) {
            }
        };
        fetchLeadSources();
        fetchCompanySizes();
    }, []);

    // Field-specific options
    const fieldOptions = {
        source: {
            label: 'Source',
            type: 'enum',
            options: leadSourceOptions,
            operators: ['equals', 'not_equals']
        },
        region: {
            label: 'Region',
            type: 'enum',
            options: [
                { value: 'North', label: 'North' },
                { value: 'South', label: 'South' },
                { value: 'East', label: 'East' },
                { value: 'West', label: 'West' },
                { value: 'Central', label: 'Central' },
                { value: 'North-East', label: 'North-East' },
                { value: 'North-West', label: 'North-West' },
                { value: 'South-East', label: 'South-East' },
                { value: 'South-West', label: 'South-West' }
            ],
            operators: ['equals', 'not_equals']
        },
        companySize: {
            label: 'Company Size',
            type: 'enum',
            options: companySizeOptions,
            operators: ['equals', 'not_equals']
        },
        budget: {
            label: 'Budget',
            type: 'number',
            operators: ['greater_than', 'less_than', 'equals', 'greater_than_equal', 'less_than_equal']
        },
        designation: {
            label: 'Role',
            type: 'text',
            operators: ['equals', 'contains', 'not_equals']
        },
        timeline: {
            label: 'Timeline',
            type: 'enum',
            options: [
                { value: 'Immediate', label: 'Immediate' },
                { value: '1-3 Months', label: '1-3 Months' },
                { value: '3-6 Months', label: '3-6 Months' },
                { value: '6+ Months', label: '6+ Months' }
            ],
            operators: ['equals', 'not_equals']
        }
    };

    const getCurrentFieldConfig = () => {
        return fieldOptions[newRule.field] || { type: 'text', operators: ['equals', 'contains'], label: newRule.field };
    };

    const formatOperator = (operator) => {
        const labels = {
            'equals': 'Equals',
            'not_equals': 'Not Equals',
            'contains': 'Contains',
            'in': 'In (Multiple)',
            'not_in': 'Not In',
            'greater_than': 'Greater Than',
            'less_than': 'Less Than',
            'greater_than_equal': 'Greater Than or Equal',
            'less_than_equal': 'Less Than or Equal',
            'between': 'Between'
        };
        return labels[operator] || operator?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || operator;
    };

    const formatValue = (field, value) => {
        if (!value) return '';
        if (typeof value === 'number') return String(value);

        const fieldConfig = fieldOptions[field];
        if (fieldConfig?.options) {
            if (field === 'companySize') {
                const option = fieldConfig.options.find(opt => 
                    String(opt.value) === String(value) || 
                    String(opt.label).toLowerCase() === String(value).toLowerCase()
                );
                if (option) return option.label;
            } else {
                const option = fieldConfig.options.find(opt => String(opt.value) === String(value));
                if (option) return option.label;
            }
        }

        return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const handleFormDataChange = (field, value, fieldType = 'text', maxLength = 100) => {
        let processedValue = value;
        if (field === 'isActive') {
            processedValue = value;
        } else if (fieldType === 'text' || fieldType === 'textarea') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        }

        setScoringFormData(prev => ({ ...prev, [field]: processedValue }));

        // Clear error
        if (scoringErrors[field]) {
            setScoringErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleFieldChange = (field) => {
        const fieldConfig = fieldOptions[field];
        setNewRule({
            field,
            operator: fieldConfig?.operators[0] || 'equals',
            condition: '',
            score: ''
        });
    };

    const handleAddRule = () => {
        setScoringErrors(prev => ({ ...prev, newRule: undefined }));

        if (newRule.field && newRule.operator && newRule.condition && newRule.score) {
            setScoringFormData(prev => ({
                ...prev,
                rules: [...prev.rules, { ...newRule }]
            }));
            setScoringErrors(prev => ({ ...prev, rules: undefined, newRule: undefined }));

            // Reset new rule
            const defaultFieldConfig = fieldOptions.source;
            setNewRule({
                field: 'source',
                operator: defaultFieldConfig?.operators[0] || 'equals',
                condition: '',
                score: ''
            });
        } else {
            setScoringErrors(prev => ({ ...prev, newRule: 'Please fill all rule fields.' }));
        }
    };

    const handleRemoveRule = (index) => {
        setScoringFormData(prev => ({
            ...prev,
            rules: prev.rules.filter((_, i) => i !== index)
        }));
    };

    const handleBlur = (field, e) => {
        if (field === 'name') {
            if (!scoringFormData.name.trim()) {
                setScoringErrors(prev => ({ ...prev, name: 'Rule name is required.' }));
            }
        } else if (field === 'rules') {
            // Check if focus is leaving the container - react synthetic event relatedTarget works here
            // We need to check if the new focused element is still inside the rules container
            // However, handling 'blur' on a div requires tabindex, or we can use onBlur on the inputs inside.
            // But 'rules' is a list. This is tricky for "container blur".
            // A simpler approach for rules is: check on save, or check when the list becomes empty IF it was previously filled?
            // But the user asked: "touch required field and not fill".
            // For a list of rules, "touching" is ambiguous. Maybe they mean focusing on the "Add Rule" section and leaving without adding?
            // Or typically, onBlur validation is best for inputs.
            // For the list, maybe we can't easily do "on-blur" unless we wrap strictly.
            // Let's implement it for 'name' first.
            // For 'rules', let's TRY to wrap the rules section in a div with onBlur.
            if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
                return;
            }
            if (scoringFormData.rules.length === 0) {
                setScoringErrors(prev => ({ ...prev, rules: 'At least one rule is required.' }));
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {selectedScoring ? 'Edit Scoring Rule' : 'Create Scoring Rule'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rule Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            value={scoringFormData.name}
                            onChange={(e) => handleFormDataChange('name', e.target.value)}
                            onBlur={(e) => handleBlur('name', e)}
                            maxLength={100}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${scoringErrors.name ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="Enter rule name"
                        />
                        <div className="flex justify-between mt-1 items-center">
                            <div>
                                {scoringErrors.name && (
                                    <p className="text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {scoringErrors.name}
                                    </p>
                                )}
                            </div>
                            <div>
                                <span className="text-xs text-gray-500">
                                    {scoringFormData.name.length}/100 characters
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            value={scoringFormData.description}
                            onChange={(e) => handleFormDataChange('description', e.target.value, 'textarea', FIELD_LIMITS.notes)}
                            maxLength={FIELD_LIMITS.notes}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${scoringErrors.description ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="Enter rule description"
                        />
                        <div className="flex justify-between mt-1 items-center">
                            <div>
                                {scoringErrors.description && (
                                    <p className="text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {scoringErrors.description}
                                    </p>
                                )}
                            </div>
                            <div>
                                <span className="text-xs text-gray-500">
                                    {scoringFormData.description.length}/{FIELD_LIMITS.notes} characters
                                </span>
                            </div>
                        </div>
                    </div>

                    <div onBlur={(e) => handleBlur('rules', e)}>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                    Scoring Rules <span className="text-red-500 ml-1">*</span>
                                </label>
                                <span className={`text-sm font-medium ${scoringFormData.rules.reduce((acc, curr) => acc + Number(curr.score), 0) > 100
                                    ? 'text-red-600'
                                    : 'text-gray-500'
                                    }`}>
                                    Total Score: {scoringFormData.rules.reduce((acc, curr) => acc + Number(curr.score), 0)}/100
                                </span>
                            </div>
                            {scoringErrors.rules && (
                                <p className="text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {scoringErrors.rules}
                                </p>
                            )}
                            {/* Existing rules */}
                            {scoringFormData.rules.map((rule, index) => (
                                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm font-medium text-gray-700 capitalize">{rule.field}</span>
                                    <span className="text-sm text-gray-500">{formatOperator(rule.operator)}</span>
                                    <span className="text-sm text-gray-700 capitalize">{formatValue(rule.field, rule.condition || rule.value)}</span>
                                    <span className="text-sm text-gray-500">→</span>
                                    <span className="text-sm font-medium text-gray-900">+{rule.score} points</span>
                                    <button
                                        onClick={() => handleRemoveRule(index)}
                                        className="text-red-500 hover:text-red-700 ml-auto"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}

                            {/* Add new rule */}
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <select
                                        value={newRule.field}
                                        onChange={(e) => handleFieldChange(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    >
                                        <option value="source">Source</option>
                                        <option value="region">Region</option>
                                        <option value="companySize">Company Size</option>
                                        <option value="budget">Budget</option>
                                        <option value="designation">Role</option>
                                        <option value="timeline">Timeline</option>
                                    </select>
                                    <select
                                        value={newRule.operator}
                                        onChange={(e) => setNewRule(prev => ({ ...prev, operator: e.target.value, condition: '' }))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    >
                                        {getCurrentFieldConfig().operators.map(op => (
                                            <option key={op} value={op}>{formatOperator(op)}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        value={newRule.score || ''}
                                        onChange={(e) => {
                                            if (e.target.value === '') {
                                                setNewRule(prev => ({ ...prev, score: '' }));
                                            } else {
                                                const numValidation = validateNumberInput(e.target.value, 100, 0);
                                                setNewRule(prev => ({ ...prev, score: numValidation.value }));
                                            }
                                        }}
                                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        placeholder="Score"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                                <div className="flex items-center space-x-3">
                                    {getCurrentFieldConfig().type === 'enum' ? (
                                        <select
                                            value={newRule.condition}
                                            onChange={(e) => setNewRule(prev => ({ ...prev, condition: e.target.value }))}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        >
                                            <option value="">Select {getCurrentFieldConfig().label}</option>
                                            {getCurrentFieldConfig().options
                                                .filter(option =>
                                                    !scoringFormData.rules.some(rule =>
                                                        rule.field === newRule.field &&
                                                        rule.operator === newRule.operator &&
                                                        String(rule.condition) === String(option.value)
                                                    )
                                                )
                                                .map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                        </select>
                                    ) : getCurrentFieldConfig().type === 'number' ? (
                                        <input
                                            type="number"
                                            value={newRule.condition}
                                            onChange={(e) => {
                                                const numValidation = validateNumberInput(e.target.value, Number.MAX_SAFE_INTEGER, 0);
                                                setNewRule(prev => ({ ...prev, condition: numValidation.value || '' }));
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                            placeholder={`Enter ${getCurrentFieldConfig().label.toLowerCase()}`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={newRule.condition}
                                            onChange={(e) => {
                                                const sanitized = sanitizeTextInput(e.target.value);
                                                setNewRule(prev => ({ ...prev, condition: sanitized.slice(0, 200) }));
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                            placeholder={`Enter ${getCurrentFieldConfig().label.toLowerCase()}`}
                                            maxLength={200}
                                        />
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Plus className="h-4 w-4" />}
                                        onClick={handleAddRule}
                                        className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                    >
                                        Add
                                    </Button>
                                </div>

                                {/* Inline Error for New Rule */}
                                {scoringErrors.newRule && (
                                    <div className="flex items-center mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
                                        <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                        <span>{scoringErrors.newRule}</span>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-3 mt-5">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isActive"
                            checked={scoringFormData.isActive}
                            onChange={(e) => handleFormDataChange('isActive', e.target.checked)}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                            Active Rule
                        </label>
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        icon={isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        onClick={onSave}
                        disabled={isSaving || !scoringFormData.name.trim() || scoringFormData.rules.length === 0}
                    >
                        {isSaving
                            ? (selectedScoring ? 'Updating...' : 'Creating...')
                            : (selectedScoring ? 'Update Rule' : 'Create Rule')
                        }
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ScoringRuleModal;
